import { defineStore } from 'pinia'
import api from '@/api/axios'
import { ref } from 'vue'

export const useAiOverviewStore = defineStore('aiOverview', () => {
  // Core state
  const loading = ref(false)
  const error = ref(null)

  // AI overview generation (v4.0 API)
  async function generateAiOverview(projectId, sessionId, options = {}) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      console.log('Generating AI overview v4.0 for session:', sessionId, 'with options:', options)
      
      // v4.0 API format: simplified request structure
      const requestPayload = {
        summaryType: options.summaryType || 'comprehensive',
        includeCategories: options.includeCategories || 'all'
      }
      
      // Add regenerate flag if requested
      if (options.regenerate || options.forceRegenerate) {
        requestPayload.regenerate = true
      }
      
      const response = await api.post(`/research/projects/${projectId}/sessions/${sessionId}/ai-overview`, requestPayload)
      
      console.log('AI overview v4.0 generation response:', response.data)
      
      // Parse v4.0 response structure (double-nested: response.data.data)
      if (response.data?.data) {
        const apiData = response.data.data
        
        // Flatten the overview object properties to root level for component access
        const overviewData = {
          // Spread overview fields (executiveSummary, dataQuality, keyFindings, analysisReadiness)
          ...apiData.overview,
          
          // Add v4.0-specific fields at root level
          categoryCompleteness: apiData.categoryCompleteness || apiData.overview?.categoryCompleteness || [],
          completenessHeatmap: apiData.completenessHeatmap || {},
          dataAssessment: apiData.dataAssessment || {},
          dataSources: apiData.dataSources || {},
          
          // Add metadata
          generatedAt: new Date().toISOString(),
          sessionId,
          projectId,
          success: response.data.success
        }
        
        // Return structure compatible with DataOverview.vue component expectations
        // Component expects to access fields directly on the returned object
        return overviewData
      }
      
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to generate AI overview'
      const errorCode = err.response?.data?.code
      
      console.error('AI overview generation error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        code: errorCode,
        data: err.response?.data,
        projectId,
        sessionId
      })
      
      // Preserve error code for entity type validation
      error.value = { message: errorMessage, code: errorCode }
      
      throw err // Throw original error to preserve response data
    } finally {
      loading.value = false
    }
  }

  async function getAiOverviewStatus(projectId, sessionId, overviewId) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      let endpoint
      if (overviewId) {
        endpoint = `/research/projects/${projectId}/sessions/${sessionId}/ai-overview/${overviewId}/status`
      } else {
        endpoint = `/research/projects/${projectId}/sessions/${sessionId}/ai-overview/status`
      }
      
      const response = await api.get(endpoint)
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get AI overview status'
      console.error('AI overview status error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function getLatestAiOverview(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      console.log('Fetching latest AI overview from API for session:', sessionId)
      
      const response = await api.get(`/research/projects/${projectId}/sessions/${sessionId}/ai-overview`)
      
      console.log('AI overview API response:', response.data)
      
      // Handle v4.0 GET response structure (response.data.data with metadata)
      if (response.data?.overview) {
        const apiData = response.data
        // Flatten overview object properties to root level
        
        const normalizedData = {
          // Spread overview fields (executiveSummary, dataQuality, keyFindings, analysisReadiness)
          ...apiData.overview,
          
          // Ensure v4.0 fields are at root level for component access
          categoryCompleteness: apiData.categoryCompleteness || apiData.overview?.categoryCompleteness || [],
          completenessHeatmap: apiData.completenessHeatmap || {},
          dataAssessment: apiData.dataAssessment || {},
          dataSources: apiData.dataSources || {},
          
          // Include GET-specific metadata field (contains savedAt, version, vectorCount, etc.)
          metadata: apiData.metadata || {},
          
          // Include generatedAt from API response (when overview was originally created)
          generatedAt: apiData.generatedAt || apiData.metadata?.savedAt || new Date().toISOString(),
          
          // Add fetch metadata
          fetchedAt: new Date().toISOString(),
          sessionId,
          projectId,
          success: response.data.success
        }
        console.log('Normalizing AI overview data structure for session:', normalizedData)
        
        return normalizedData
      } else if (response.data?.overview) {
        // Fallback for legacy single-nested structure (response.data.overview)
        const normalizedData = {
          ...response.data.overview,
          categoryCompleteness: response.data.categoryCompleteness || response.data.overview?.categoryCompleteness || [],
          completenessHeatmap: response.data.completenessHeatmap || {},
          dataAssessment: response.data.dataAssessment || {},
          metadata: response.data.metadata || {},
          generatedAt: response.data.generatedAt || new Date().toISOString(),
          fetchedAt: new Date().toISOString(),
          sessionId,
          projectId
        }
        
        return normalizedData
      }
      
      return response.data
    } catch (err) {
      const status = err.response?.status
      const errorData = err.response?.data
      
      // Handle 404 - no overview exists yet
      if (status === 404) {
        console.log('No AI overview found for session:', sessionId)
        return {
          exists: false,
          sessionId,
          projectId,
          message: 'No AI overview generated yet'
        }
      }
      
      const errorMessage = errorData?.message || err.message || 'Failed to get AI overview'
      console.error('AI overview fetch error:', {
        status,
        statusText: err.response?.statusText,
        data: errorData,
        projectId,
        sessionId
      })
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function regenerateAiOverview(projectId, sessionId, options = {}) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    // Generate new overview
    return await generateAiOverview(projectId, sessionId, {
      ...options,
      forceRegenerate: true
    })
  }

  async function getAiOverviewHistory(projectId, sessionId, limit = 10) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/research/projects/${projectId}/sessions/${sessionId}/ai-overview/history`, {
        params: { limit }
      })
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get AI overview history'
      console.error('AI overview history error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function exportAiOverview(projectId, sessionId, overviewId, format = 'pdf') {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      let endpoint
      if (overviewId) {
        endpoint = `/research/projects/${projectId}/sessions/${sessionId}/ai-overview/${overviewId}/export`
      } else {
        endpoint = `/research/projects/${projectId}/sessions/${sessionId}/ai-overview/export`
      }
      
      const response = await api.get(endpoint, {
        params: { format },
        responseType: format === 'pdf' ? 'blob' : 'json'
      })
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to export AI overview'
      console.error('AI overview export error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function deleteAiOverview(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      console.log('Deleting AI overview for session:', sessionId)
      
      await api.delete(`/research/projects/${projectId}/sessions/${sessionId}/ai-overview`)
      
      console.log('AI overview deleted successfully')
      return { success: true }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete AI overview'
      console.error('AI overview delete error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    loading,
    error,
    
    // AI overview operations
    generateAiOverview,
    getAiOverviewStatus,
    getLatestAiOverview,
    regenerateAiOverview,
    deleteAiOverview,
    getAiOverviewHistory,
    exportAiOverview
  }
})